import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang, scrollTo } from "@/App";

const HERO_IMG = "https://prieure-saint-leonard.fr/api/files/prieure-saint-leonard/uploads/55b4b5d7-710a-45d6-bc91-1688822f211a.png";

const MaskedLine = ({ children, delay, className }) => (
  <span className={`block overflow-hidden ${className || ""}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { lang, t } = useLang();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 260]);
  const scale = useTransform(scrollY, [0, 900], [1.08, 1.22]);

  return (
    <section id="top" data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-inkdeep">
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <img src={HERO_IMG} alt="Baie du Mont-Saint-Michel" className="w-full h-full object-cover img-lumiere" />
      </motion.div>
      <div className="absolute inset-0 hero-vignette" />
      <div className="absolute inset-x-0 top-0 h-56 bg-gradient-to-b from-inkdeep/60 via-inkdeep/25 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-paper/0" />

      <div key={lang} className="relative z-10 h-full max-w-[1600px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-24 md:pb-28">
        <MaskedLine delay={0.3}>
          <p data-testid="hero-overline" className="inline-block font-mono2 text-[10px] md:text-xs uppercase tracking-[0.35em] text-gold font-medium mb-6 md:mb-8 bg-inkdeep/40 backdrop-blur-sm px-3 py-2 border border-gold/25 [text-shadow:_0_1px_14px_rgba(23,19,16,0.95)]">
            {t.hero.overline}
          </p>
        </MaskedLine>

        <h1 data-testid="hero-section-title" className="font-display font-light text-paper leading-[1.02] tracking-tight [text-shadow:_0_2px_28px_rgba(23,19,16,0.65)]">
          <MaskedLine delay={0.45} className="text-4xl sm:text-6xl lg:text-7xl xl:text-8xl">
            {t.hero.line1}
          </MaskedLine>
          <MaskedLine delay={0.62} className="text-4xl sm:text-6xl lg:text-7xl xl:text-8xl">
            <span className="italic text-terraBright [text-shadow:_0_2px_18px_rgba(23,19,16,0.55)]">{t.hero.line2}</span>
          </MaskedLine>
        </h1>

        <div className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0 }}
          >
            <p data-testid="hero-name" className="font-display text-xl md:text-2xl text-paper mb-2 [text-shadow:_0_1px_16px_rgba(23,19,16,0.85)]">
              Gilles de Buretel de Chassey
            </p>
            <p data-testid="hero-intro" className="max-w-md text-paper/80 text-sm md:text-base leading-relaxed [text-shadow:_0_1px_14px_rgba(23,19,16,0.85)]">
              {t.hero.intro}
            </p>
          </motion.div>

          <motion.div
            className="flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
          >
            <button
              data-testid="hero-cta-sculptures-button"
              onClick={() => scrollTo("sculptures")}
              className="group inline-flex items-center gap-3 bg-terra text-paper hover:bg-terraBright transition-colors px-7 py-3.5 font-mono2 text-[11px] uppercase tracking-[0.2em]"
            >
              {t.hero.ctaPrimary}
              <ArrowDown size={14} className="group-hover:translate-y-1 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-book-button"
              onClick={() => scrollTo("livre")}
              className="inline-flex items-center gap-3 border border-paper/40 text-paper hover:border-gold hover:text-gold transition-colors px-7 py-3.5 font-mono2 text-[11px] uppercase tracking-[0.2em]"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollTo("biographie")}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-paper/70 hover:text-gold transition-colors [text-shadow:_0_1px_10px_rgba(23,19,16,0.9)]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        aria-label={t.hero.scroll}
      >
        <span className="font-mono2 text-[9px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <span className="w-px h-8 bg-current animate-pulse-soft" />
      </motion.button>
    </section>
  );
};

export default Hero;
